import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { AdminHubCard, AdminHubGroup, AdminTabId } from "./admin-nav";
import { ADMIN_DOMAIN_NAV, isAdminHubTab, resolveAdminDomain } from "./admin-nav";
import { cn } from "@/lib/utils";

function HubCard({
  card,
  onTab,
  badge,
}: {
  card: AdminHubCard;
  onTab: (id: AdminTabId) => void;
  badge?: number;
}) {
  const Icon = card.icon;
  const className = cn(
    "flex h-full flex-col gap-2 rounded-xl border border-border/60 bg-card p-4 text-left transition-colors",
    "hover:border-primary/40 hover:bg-primary/5",
  );
  const body = (
    <>
      <div className="flex items-center justify-between">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </div>
        {badge != null && badge > 0 && (
          <Badge variant="secondary" className="h-5 min-w-5 justify-center px-1.5 text-[10px]">
            {badge}
          </Badge>
        )}
      </div>
      <p className="text-sm font-semibold text-foreground">{card.title}</p>
      <p className="text-xs leading-relaxed text-muted-foreground">{card.description}</p>
    </>
  );
  if (card.href) {
    return (
      <Link to={card.href as never} className={className}>
        {body}
      </Link>
    );
  }
  return (
    <button type="button" className={className} onClick={() => card.tab && onTab(card.tab)}>
      {body}
    </button>
  );
}

export function AdminDomainHub({
  title,
  description,
  cards,
  groups,
  onTab,
  badges,
}: {
  title: string;
  description: string;
  cards: AdminHubCard[];
  groups?: AdminHubGroup[];
  onTab: (id: AdminTabId) => void;
  /** Badges numéricos opcionales por id de tarjeta (ej. leads pendientes). */
  badges?: Record<string, number | undefined>;
}) {
  const byId = new Map(cards.map((c) => [c.id, c]));
  const grouped = groups?.map((g) => ({
    title: g.title,
    cards: g.cardIds.map((id) => byId.get(id)).filter((c): c is AdminHubCard => Boolean(c)),
  }));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>
      {grouped && grouped.length > 0 ? (
        grouped.map((g) => (
          <div key={g.title} className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.title}</h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {g.cards.map((card) => (
                <HubCard key={card.id} card={card} onTab={onTab} badge={badges?.[card.id]} />
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((card) => (
            <HubCard key={card.id} card={card} onTab={onTab} badge={badges?.[card.id]} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminHubBack({
  tab,
  onTab,
}: {
  tab: AdminTabId;
  onTab: (id: AdminTabId) => void;
}) {
  if (isAdminHubTab(tab) || tab === "dashboard" || tab === "releases") return null;
  const domain = resolveAdminDomain({ tab });
  const item = ADMIN_DOMAIN_NAV.find((d) => d.id === domain);
  if (!item || item.targetTab === tab) return null;
  return (
    <button
      type="button"
      onClick={() => onTab(item.targetTab)}
      className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-3.5 w-3.5" />
      {item.label}
    </button>
  );
}
